// App.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import FullPageLoader from './components/FullPageLoader';
import CustomerDetailsForm from './components/CustomerDetailsForm';
import DocumentUploadForm from './components/DocumentUploadForm';
import ReviewSection from './components/ReviewSection';
import AdditionalInfoForm from './components/AdditionalInfoForm';
import PreviewDialog from './components/PreviewDialog';
import DraftIndicator from './components/DraftIndicator';
import useAutoSave from './hooks/useAutoSave';
import { getCompleteFormState, applyFormState, shouldShowDraftDialog } from './utils/formStateManager';
import { getProgressSummary } from './utils/stepValidation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const API_TOKEN = process.env.REACT_APP_API_SECRET_TOKEN

function parseAddress(address) {
  const result = {
    city: '',
    district: '',
    state: '',
    country: 'India',
    pin: ''
  };

  if (!address) return result;

  const parts = address.split(',').map(p => p.trim()).filter(Boolean);
  const pinMatch = address.match(/\b\d{6}\b/);
  if (pinMatch) result.pin = pinMatch[0];

  const lower = address.toLowerCase();

  // Try to extract state
  const states = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
    'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
    'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
    'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana',
    'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi',
    'Jammu and Kashmir', 'Ladakh'
  ];
  for (let state of states) {
    if (lower.includes(state.toLowerCase())) {
      result.state = state;
      break;
    }
  }

  // Try to extract district - assumption: 2nd last part before state
  if (parts.length >= 3) {
    result.district = parts[parts.length - 3];
  }

  // Try to extract city - assumption: 3rd last or one containing 'city' or well-known ones
  if (parts.length >= 4) {
    result.city = parts[parts.length - 4];
  }

  return result;
}

// Theme
const theme = createTheme({
  palette: {
    primary: { main: '#0052B1' },
    secondary: { main: '#B6924A' }
  }
});

// Steps
const steps = [
  'Customer Details',
  'Document Uploads',
  'Review',
  'Additional Information'
];

// Country codes
const countryCodes = [
  { code: '+91', label: '🇮🇳 India' },
  { code: '+1', label: '🇺🇸 USA' },
  { code: '+44', label: '🇬🇧 UK' },
];

export default function App() {
  const [activeStep, setActiveStep] = useState(0);

  // Get lead ID from URL
  const params = new URLSearchParams(window.location.search);
  const leadId = params.get('lead_id');

  // Initialize auto-save hook
  const { draftInfo, updateDraftData, loadDraft, clearDraft, saveDraft, hideRestoreDialog } = useAutoSave(leadId);

  const sessionItem = JSON.parse(localStorage.getItem('formData_' + leadId)) || {};
  // Step 0
  const [formData, setFormData] = useState({
    statusOfCustomer: sessionItem?.statusOfCustomer || '',
    gstStatus: sessionItem?.gstStatus ||'',
    gstRegistrationType: sessionItem?.gstRegistrationType || '',
  });

  localStorage.setItem('formData_' + leadId, JSON.stringify(formData));
  

  // Step 1
  const [gstFile, setGstFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [chequeFile, setChequeFile] = useState(null);
  const [digitalSigFile, setDigitalSigFile] = useState(null);

  const [gstDetails, setGstDetails] = useState(null);
  const [panDetails, setPanDetails] = useState(null);
  const [chequeDetails, setChequeDetails] = useState({});
  const [tanDetails, setTanDetails] = useState({ tanNumber: '' });
  const [manualTanNumber, setManualTanNumber] = useState('');

  const [gstFileLink, setGstFileLink] = useState(null);
  const [panFileLink, setPanFileLink] = useState(null);
  const [chequeFileLink, setChequeFileLink] = useState(null);

  const [declarationFile, setDeclarationFile] = useState(null);
  const [declarationFileLink, setDeclarationFileLink] = useState('');
  const [altDocType, setAltDocType] = useState('');
  const [altDocFile, setAltDocFile] = useState(null);
  const [altDocFileLink, setAltDocFileLink] = useState('');
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [aadhaarDetails, setAadhaarDetails] = useState(null);
  const [aadhaarFileLink, setAadhaarFileLink] = useState('');

  // for Private-entity identifier
  const [entityIdType, setEntityIdType]       = useState(() => localStorage.getItem('entityIdType_' + leadId) || '');
  const [entityIdNumber, setEntityIdNumber]   = useState(() => localStorage.getItem('entityIdNumber_' + leadId) || '');

  // Step 1 uploads for TAN and Incorporation
  const [tanFile, setTanFile]                 = useState(null);
  const [incFile, setIncFile]                 = useState(null);

  // their S3 links
  const [tanFileLink, setTanFileLink]         = useState('');
  const [incFileLink, setIncFileLink]         = useState('');

  const [partnershipFile, setPartnershipFile] = useState(null);
  const [partnershipFileLink, setPartnershipFileLink] = useState('');
  const [passbookFile, setPassbookFile] = useState(null);
  const [passbookFileLink, setPassbookFileLink] = useState('');
  const [addressFile, setAddressFile] = useState(null);
  const [swiftFile, setSwiftFile] = useState(null);
  const [addressFileLink, setAddressFileLink] = useState('');
  const [swiftFileLink, setSwiftFileLink] = useState('');
  const [mandateFile, setMandateFile] = useState(null);
  const [mandateFileLink, setMandateFileLink] = useState('');
  const [errorMsg, setErrorMsg] = useState("You've already submitted data.");
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [additionalAddress, setAdditionalAddress] = useState({
    address: '',
    city: '',
    district: '',
    state: '',
    pincode: ''
  });

  const [hasNoCheque, setHasNoCheque] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    ifsc: '',
    accountNumber: '',
    chequeNumber: ''
  });

  const [bankDialogOpen, setBankDialogOpen] = useState(false);


  const handleCheckboxCheque = (event) => {
    const isChecked = event.target.checked;
    setHasNoCheque(isChecked);

    let newBankDetails = bankDetails;
    let newMandateFile = mandateFile;
    let newMandateFileLink = mandateFileLink;
    let newChequeDetails = chequeDetails;

    if (isChecked) {
      setChequeFile(null); // Clear cheque file
      setBankDialogOpen(true); // Open dialog
      setChequeDetails({});
      newChequeDetails = {};
    } else {
      const clearedBankDetails = { bankName: '', ifsc: '', accountNumber: '', chequeNumber: '' };
      setBankDetails(clearedBankDetails); // Clear bank details
      setMandateFileLink(''); // Clear mandate file link
      setMandateFile(null); // Clear mandate file
      newBankDetails = clearedBankDetails;
      newMandateFile = null;
      newMandateFileLink = '';
    }
    
    // Auto-save cheque checkbox changes
    updateDraftData(getCompleteFormState({
      activeStep, formData, entityIdType, entityIdNumber, entityIdError,
      gstFile, panFile, chequeFile: null, tanFile, incFile, declarationFile,
      partnershipFile, passbookFile, addressFile, swiftFile, aadhaarFile, altDocFile, mandateFile: newMandateFile,
      gstFileLink, panFileLink, chequeFileLink, tanFileLink, incFileLink, declarationFileLink,
      partnershipFileLink, passbookFileLink, addressFileLink, swiftFileLink, aadhaarFileLink, altDocFileLink, mandateFileLink: newMandateFileLink,
      gstDetails, panDetails, chequeDetails: newChequeDetails, tanDetails, aadhaarDetails,
      bankDetails: newBankDetails, hasNoCheque: isChecked, altDocType, additionalAddress, checked, additional, leadId, manualTanNumber
    }));
  };

  const handleBankInputChange = (field) => (event) => {
    const newBankDetails = { ...bankDetails, [field]: event.target.value };
    setBankDetails(newBankDetails);
    
    // Auto-save bank details changes
    updateDraftData(getCompleteFormState({
      activeStep, formData, entityIdType, entityIdNumber, entityIdError,
      gstFile, panFile, chequeFile, tanFile, incFile, declarationFile,
      partnershipFile, passbookFile, addressFile, swiftFile, aadhaarFile, altDocFile, mandateFile,
      gstFileLink, panFileLink, chequeFileLink, tanFileLink, incFileLink, declarationFileLink,
      partnershipFileLink, passbookFileLink, addressFileLink, swiftFileLink, aadhaarFileLink, altDocFileLink, mandateFileLink,
      gstDetails, panDetails, chequeDetails, tanDetails, aadhaarDetails,
      bankDetails: newBankDetails, hasNoCheque, altDocType, additionalAddress, checked, additional, leadId, manualTanNumber
    }));
  };

  const handleBankDialogClose = () => {
    setBankDialogOpen(false);
    if (isFormEmpty(bankDetails)) {
      // If bank details are empty, uncheck the checkbox
      setHasNoCheque(false);
    }

  };

  const isFormEmpty = (data) => {
    return Object.values(data).every(val => val.trim() === '');
  };

  const handleCheckboxChange = (event) => {
    const isChecked = event.target.checked;

    if (!isChecked) {
      // Clear form and close popup when unchecked
      setAdditionalAddress({
        address: '',
        city: '',
        district: '',
        state: '',
        pincode: ''
      });
      setOpen(false);
    } else {
      setOpen(true);
    }

    setChecked(isChecked);
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    const updated = { ...additionalAddress, [field]: value };
    setAdditionalAddress(updated);

    // Auto-check checkbox if any field has a value
    const anyFieldFilled = Object.values(updated).some(val => val.trim() !== '');
    setChecked(anyFieldFilled);
    setOpen(anyFieldFilled);
  };

  const handleClose = () => {
    setOpen(false);
     if (isFormEmpty(additionalAddress)) {
      // Uncheck if form is empty
      setChecked(false);
    }
  };

  // Step 3
  const [additional, setAdditional] = useState({
    email: '',
    mobileCode: '+91',
    mobile: '',
    contactName: '',
    contactDesignation: '',
    contactDepartment: '',
    contactMobileCode: '+91',
    contactMobile: '',
    authorisedSignatoryName: '',
    authorisedSignatoryMobile: '',
    authorisedSignatoryEmail: '',
    authorisedSignatoryDesignation: '',
    certifyTrue: false,
    acceptPrivacy: false,
    a1_flag: false,
    a2_flag: false,
  });

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'error' });

  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLabel, setPreviewLabel] = useState('');

  const allOptions = {
    'Private Limited/ Public Limited': ['CIN Number'],
    'LLP': ['LLP Number'],
    'Registered Partnership': ['Partnership Deed Number'],
  };

  const [entityTypeOptions, setEntityTypeOptions] = useState([
    'CIN Number',
    'LLP Number',
    'Partnership Deed Number',
  ]);

  const statusOptions = [
    'Proprietor / Individual',
    'Private Limited/ Public Limited',
    'Society/AOP/HUF/Trust',
    'LLP',
    'Registered Partnership',
    'Unregistered Partnership',
    'Foreign Entity'
  ];

  const gstStatusOptions = ['Normal Registered', 'Un-Registered'];
  const gstRegTypeOptions = ['Active', 'Migrated', 'Not Applicable', 'Provisional'];
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  // leadId already declared above

  // on mount, check localStorage for prior submit
  // ── on mount: if there's a leadId, fetch it and see if it's already filled ──

  
  useEffect(() => {
    if (!leadId){
      setErrorMsg('Invalid URL.');
      setAlreadySubmitted(true);
    };

    fetch(`${API_URL}/onboarding-lead?lead_id=${leadId}`, {
      headers: {
          'X-API-TOKEN': API_TOKEN
      }
    }
  )
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Server error: ${res.status}`);
        }

        const data = await res.json();

        // If API responds with an error field
        if (data.error) {
          throw new Error(data.error);
        }

        const lead = data.lead;
        // Only mark as submitted if PAN is uploaded (adjust condition as needed)

        if ( ( lead?.stage?.identifier === 'start' ) ||
          ( lead?.custom_field_values?.copy_of_pan_card?.url &&
            lead?.custom_field_values?.sales_team_approval_status?.name === 'Approved' &&
            (
              !lead?.custom_field_values?.finance_team_approval_status?.name || 
              lead?.custom_field_values?.finance_team_approval_status?.name === 'Approved' )
          )
        ) {
          setAlreadySubmitted(true);
        }


        if( lead?.stage?.id === 46173 && lead?.custom_field_values?.sales_team_approval_status?.name === 'Approved'
          && lead?.custom_field_values?.finance_team_approval_status?.name === 'Rejected'
        ){
          setAlreadySubmitted(true);
        }

        setFormData(
          {
            statusOfCustomer: lead?.custom_field_values?.status_of_customer?.name || '',
            gstStatus: lead?.custom_field_values?.gst_status?.name || '',
            gstRegistrationType: lead?.custom_field_values?.gst_registration_type?.name || '',
          }
        );

        // setGstFileLink(lead?.custom_field_values?.copy_of_gst_registration_certificate?.url || '');
        // setPanFileLink(lead?.custom_field_values?.copy_of_pan_card?.url || '');
        // setChequeFileLink(lead?.custom_field_values?.colour_copy_of_cancelled_cheque?.url || '');
        // setTanFileLink(lead?.custom_field_values?.copy_of_tan_registration?.url || '');
        // setTanDetails({ tanNumber: lead?.custom_field_values?.tan || '' });

        setAdditional({
            email: lead?.custom_field_values?.enter_the_email_id_you_want_to_send_the_cvf_to ||'',
            mobileCode: '+91',
            mobile: lead?.custom_field_values?.mobile_number1?.replace(/^\+91\s?/, '') || '',
            contactName: lead?.custom_field_values?.name1 || '',
            contactDesignation: lead?.custom_field_values?.designation ||'',
            contactDepartment: lead?.custom_field_values?.department ||'',
            contactMobileCode: '+91',
            contactMobile: lead?.custom_field_values?.mobile_number1?.replace(/^\+91\s?/, '') || '',
            authorisedSignatoryName: lead?.custom_field_values?.name2 || '',
            authorisedSignatoryMobile: lead?.custom_field_values?.mobile_number3?.replace(/^\+91\s?/, '') || '',
            authorisedSignatoryEmail: lead?.custom_field_values?.email_id || '',
            authorisedSignatoryDesignation: lead?.custom_field_values?.designation1 ||  ''
          });

          if(lead.custom_field_values.status_of_customer && lead.custom_field_values.status_of_customer?.name !== 'undefined'){
            if(!lead.custom_field_values.a1_flag && !lead.custom_field_values.a2_flag){
               setAdditional({
                email: lead?.custom_field_values?.enter_the_email_id_you_want_to_send_the_cvf_to ||'',
                mobileCode: '+91',
                mobile: lead?.custom_field_values?.mobile_number1?.replace(/^\+91\s?/, '') || '',
                contactName: lead?.custom_field_values?.name1 || '',
                contactDesignation: lead?.custom_field_values?.designation ||'',
                contactDepartment: lead?.custom_field_values?.department ||'',
                contactMobileCode: '+91',
                contactMobile: lead?.custom_field_values?.mobile_number1?.replace(/^\+91\s?/, '') || '',
                authorisedSignatoryName: lead?.custom_field_values?.name2 || '',
                authorisedSignatoryMobile: lead?.custom_field_values?.mobile_number3?.replace(/^\+91\s?/, '') || '',
                authorisedSignatoryEmail: lead?.custom_field_values?.email_id || '',
                authorisedSignatoryDesignation: lead?.custom_field_values?.designation1 ||  '',
                a1_flag: true 
              });
            }else if(!lead.custom_field_values.a2_flag){
               setAdditional({
                email: lead?.custom_field_values?.enter_the_email_id_you_want_to_send_the_cvf_to ||'',
                mobileCode: '+91',
                mobile: lead?.custom_field_values?.mobile_number1?.replace(/^\+91\s?/, '') || '',
                contactName: lead?.custom_field_values?.name1 || '',
                contactDesignation: lead?.custom_field_values?.designation ||'',
                contactDepartment: lead?.custom_field_values?.department ||'',
                contactMobileCode: '+91',
                contactMobile: lead?.custom_field_values?.mobile_number1?.replace(/^\+91\s?/, '') || '',
                authorisedSignatoryName: lead?.custom_field_values?.name2 || '',
                authorisedSignatoryMobile: lead?.custom_field_values?.mobile_number3?.replace(/^\+91\s?/, '') || '',
                authorisedSignatoryEmail: lead?.custom_field_values?.email_id || '',
                authorisedSignatoryDesignation: lead?.custom_field_values?.designation1 ||  '',
                a2_flag: true 
                });
            }
          }

      })
      .catch((err) => {
        console.warn('Fetch or API error:', err.message);
        // On error, treat as already submitted
        setAlreadySubmitted(true);
        setErrorMsg('Something went wrong. Please try again later.');
      });
      setLoading(false);
  }, [leadId]);

  const [entityIdError, setEntityIdError] = useState('');

  function validateEntityId(type, value) {
    if (!value) return false;
    switch (type) {
      case 'CIN Number':
        // e.g. L01631KA2010PTC096843
        return /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(value);
      case 'LLP Number':
        // either 7 alphanumeric chars (e.g. AAA0002) or the common AAA-0002 format
        return (
          /^[A-Za-z0-9]{7}$/.test(value) ||
          /^[A-Za-z]{3}-\d{4}$/.test(value)
        );
      case 'Partnership Deed Number':
        // simple alphanumeric minimum 5 chars
        return /^[A-Za-z0-9]{5,}$/.test(value);
      default:
        return true;
    }
  }

  // Step 0 handlers
  const handleChange = e => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };
    setFormData(newFormData);
    
    if (name === 'gstRegistrationType' && value !== 'Active') {
      setGstFile(null);
      setPanFile(null);
      setChequeFile(null);
      setDigitalSigFile(null);
      setGstDetails(null);
      setPanDetails(null);
      setChequeDetails({});
    }

    if (name === 'statusOfCustomer') {
      setEntityTypeOptions(allOptions[value] || []);
      setEntityIdType(allOptions[value]);
      setEntityIdNumber('');
    }
    
    // Auto-save form changes
    updateDraftData(getCompleteFormState({
      activeStep, formData: newFormData, entityIdType, entityIdNumber, entityIdError,
      gstFile, panFile, chequeFile, tanFile, incFile, declarationFile,
      partnershipFile, passbookFile, addressFile, swiftFile, aadhaarFile, altDocFile, mandateFile,
      gstFileLink, panFileLink, chequeFileLink, tanFileLink, incFileLink, declarationFileLink,
      partnershipFileLink, passbookFileLink, addressFileLink, swiftFileLink, aadhaarFileLink, altDocFileLink, mandateFileLink,
      gstDetails, panDetails, chequeDetails, tanDetails, aadhaarDetails,
      bankDetails, hasNoCheque, altDocType, additionalAddress, checked, additional, leadId, manualTanNumber
    }));
  };

  // Helper function to map component keys to API endpoint names
  const getEndpointType = (type) => {
    const typeMapping = {
      'inc': 'incorporation',
      'altDoc': 'passport', // This will be overridden based on altDocType
      // Other types remain the same
    };
    
    // Special handling for alternative documents
    if (type === 'altDoc') {
      return altDocType; // passport, dl, voter_id
    }
    
    return typeMapping[type] || type;
  };

  // Step 1 handlers (with OCR validation)
  const handleFileUpload = (type) => async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Frontend file type validation
    // if (type === 'gst' && file.type !== 'application/pdf') {
    //   setSnack({
    //     open: true,
    //     message: 'GST Certificate must be a PDF file.',
    //     severity: 'error'
    //   });
    //   return;
    // }
    // const maxSize = 5 * 1024 * 1024; 
    // if (file.size > maxSize) {
    //   setSnack({
    //     open: true,
    //     message: 'File size exceeds 5 MB. Please upload a smaller file.',
    //     severity: 'error'
    //   });
    //   return;
    // }

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setSnack({
        open: true,
        message: 'Unsupported file format. Please upload an image or PDF file.',
        severity: 'error'
      });
      return;
    }

    // 1) Put the raw File into React state so previews work
    switch (type) {
      case 'gst':          setGstFile(file); break;
      case 'pan':          setPanFile(file); break;
      case 'cheque':       setChequeFile(file); break;
      case 'aadhaar':      setAadhaarFile(file); break;
      case 'declaration':  setDeclarationFile(file); break;
      case 'tan':          setTanFile(file); break;
      case 'inc':          setIncFile(file); break; // Fixed: was 'incorporation'
      case 'incorporation':setIncFile(file); break; // Keep both for compatibility
      case 'passbook':     setPassbookFile(file); break;
      case 'address':      setAddressFile(file); break;
      case 'mandate':      setMandateFile(file); break;
      case 'swift':        setSwiftFile(file); break;
      case 'altDoc':       setAltDocFile(file); break; // Fixed: unified alternative docs
      case 'passport':     setAltDocFile(file); break;
      case 'dl':           setAltDocFile(file); break;
      case 'voter_id':     setAltDocFile(file); break;
      default: break;
    }

    // 2) Build & send the FormData
    const endpointType = getEndpointType(type);
    const form = new FormData();
    
    // Use the endpoint type for FormData field name
    if (['passport', 'dl', 'voter_id'].includes(endpointType)) {
      form.append(`${endpointType}file`, file);
    } else {
      form.append(`${endpointType}file`, file);
    }

    const url = `${API_URL}/upload-${endpointType}`;
    setLoading(true);
    try {
      const res = await fetch(url, { method: 'POST', body: form, 
        headers: {
          'X-API-TOKEN': API_TOKEN
        }
        });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');

      // 3) On success, pull out the details or URL
      let newGstDetails = gstDetails;
      let newPanDetails = panDetails;
      let newChequeDetails = chequeDetails;
      let newTanDetails = tanDetails;
      let newAadhaarDetails = aadhaarDetails;
      let newGstFileLink = gstFileLink;
      let newPanFileLink = panFileLink;
      let newChequeFileLink = chequeFileLink;
      let newTanFileLink = tanFileLink;
      let newIncFileLink = incFileLink;
      let newDeclarationFileLink = declarationFileLink;
      let newPartnershipFileLink = partnershipFileLink;
      let newPassbookFileLink = passbookFileLink;
      let newAddressFileLink = addressFileLink;
      let newSwiftFileLink = swiftFileLink;
      let newAadhaarFileLink = aadhaarFileLink;
      let newAltDocFileLink = altDocFileLink;
      let newMandateFileLink = mandateFileLink;

      switch (type) {
        case 'gst':
          newGstDetails = data.gstDetails;
          newGstFileLink = data.fileUrl;
          setGstDetails(data.gstDetails);
          setGstFileLink(data.fileUrl);
          break;
        case 'pan':
          newPanDetails = data.panDetails;
          newPanFileLink = data.fileUrl;
          setPanDetails(data.panDetails);
          setPanFileLink(data.fileUrl);
          break;
        case 'cheque':
          newChequeDetails = data.chequeDetails;
          newChequeFileLink = data.fileUrl;
          setChequeDetails(data.chequeDetails);
          setChequeFileLink(data.fileUrl);
          break;
        case 'aadhaar':
          newAadhaarDetails = data.aadhaarDetails;
          newAadhaarFileLink = data.fileUrl;
          setAadhaarDetails(data.aadhaarDetails);
          setAadhaarFileLink(data.fileUrl);
          break;
        case 'declaration':
          newDeclarationFileLink = data.fileUrl;
          setDeclarationFileLink(data.fileUrl);
          break;
        case 'tan':
          newTanDetails = data.tanDetails;
          newTanFileLink = data.fileUrl;
          setTanDetails(data.tanDetails);
          setTanFileLink(data.fileUrl);
          break;
        case 'inc': // Fixed: handle the component key
        case 'incorporation':
          newIncFileLink = data.fileUrl;
          setIncFileLink(data.fileUrl);
          break;
        case 'partnership':
          newPartnershipFileLink = data.fileUrl;
          setPartnershipFile(file);
          setPartnershipFileLink(data.fileUrl);
          break;
        case 'passbook':
          newPassbookFileLink = data.fileUrl;
          setPassbookFile(file);
          setPassbookFileLink(data.fileUrl);
          break;
        case 'address':
          newAddressFileLink = data.fileUrl;
          setAddressFile(file);
          setAddressFileLink(data.fileUrl);
          break;
        case 'mandate':
          newMandateFileLink = data.fileUrl;
          setMandateFile(file);
          setMandateFileLink(data.fileUrl);
          break;
        case 'swift':
          newSwiftFileLink = data.fileUrl;
          setSwiftFile(file);
          setSwiftFileLink(data.fileUrl);
          break;
        case 'altDoc': // Fixed: handle the component key for alternative docs
        case 'passport':
        case 'dl':
        case 'voter_id':
          newAltDocFileLink = data.fileUrl;
          setAltDocFileLink(data.fileUrl);
          break;
        default:
          console.warn(`No handler for upload type "${type}"`);
      }
      if (type === 'gst' && !data.gstDetails?.registrationNumber) throw new Error('GST registration number could not be extracted. Please upload a clear PDF of the GST certificate.');
      if (type === 'pan' && !data.panDetails?.panNumber) throw new Error('PAN number could not be extracted. Please upload a clear image of the PAN card.');
      if (type === 'cheque' && !data.chequeDetails?.ifsc) throw new Error('IFSC code could not be extracted. Please upload a clearer image of the cancelled cheque.');
      // For TAN, we allow manual entry if extraction fails, so we show a warning instead of throwing an error
      if (type === 'tan' && !data.tanDetails?.tanNumber) {
        setSnack({
          open: true,
          message: 'TAN number could not be extracted automatically. Please enter it manually in the field below.',
          severity: 'warning'
        });
      }
      if (type === 'aadhaar' && !data.aadhaarDetails?.address) throw new Error('Address could not be extracted from the Aadhaar card. Please upload a clearer image.');
      
      // Auto-save after successful upload
      updateDraftData(getCompleteFormState({
        activeStep, formData, entityIdType, entityIdNumber, entityIdError,
        gstFile, panFile, chequeFile, tanFile, incFile, declarationFile,
        partnershipFile, passbookFile, addressFile, swiftFile, aadhaarFile, altDocFile, mandateFile,
        gstFileLink: newGstFileLink, panFileLink: newPanFileLink, chequeFileLink: newChequeFileLink, 
        tanFileLink: newTanFileLink, incFileLink: newIncFileLink, declarationFileLink: newDeclarationFileLink,
        partnershipFileLink: newPartnershipFileLink, passbookFileLink: newPassbookFileLink, 
        addressFileLink: newAddressFileLink, swiftFileLink: newSwiftFileLink, aadhaarFileLink: newAadhaarFileLink, 
        altDocFileLink: newAltDocFileLink, mandateFileLink: newMandateFileLink,
        gstDetails: newGstDetails, panDetails: newPanDetails, chequeDetails: newChequeDetails, 
        tanDetails: newTanDetails, aadhaarDetails: newAadhaarDetails,
        bankDetails, hasNoCheque, altDocType, additionalAddress, checked, additional, leadId, manualTanNumber
      }));
    } catch (err) {
      console.error(`[handleFileUpload] ERROR for ${type}:`, err);
      setSnack({
        open: true,
        message: `Invalid ${type.toUpperCase()} document: ${err.message}`,
        severity: 'error'
      });

      // 4) reset only the one that failed
      switch (type) {
        case 'gst':
          setGstFile(null); setGstDetails(null); setGstFileLink('');
          break;
        case 'pan':
          setPanFile(null); setPanDetails(null); setPanFileLink('');
          break;
        case 'cheque':
          setChequeFile(null); setChequeDetails({}); setChequeFileLink('');
          break;
        case 'aadhaar':
          setAadhaarFile(null); setAadhaarDetails(null); setAadhaarFileLink('');
          break;
        case 'declaration':
          setDeclarationFile(null); setDeclarationFileLink('');
          break;
        case 'tan':
          setTanFile(null); setTanDetails(null); setTanFileLink('');
          break;
        case 'inc': // Fixed: handle component key
        case 'incorporation':
          setIncFile(null); setIncFileLink('');
          break;
        case 'altDoc': // Fixed: handle component key for alternative docs
        case 'passport':
        case 'dl':
        case 'voter_id':
          setAltDocFile(null); setAltDocFileLink('');
          break;
        default:
          break;
      }
    }

    setLoading(false);
  };

  // Digital signature (no OCR)
  // const handleDigitalSignatureUpload = e => {
  //   const file = e.target.files[0];
  //   if (file) setDigitalSigFile(file);
  // };

  // Step 3 handlers
  const handleAdditionalChange = e => {
    const { name, value, type, checked } = e.target;
    const newAdditional = {
      ...additional,
      [name]: type === 'checkbox' ? checked : value
    };
    setAdditional(newAdditional);
    
    // Auto-save additional info changes
    updateDraftData(getCompleteFormState({
      activeStep, formData, entityIdType, entityIdNumber, entityIdError,
      gstFile, panFile, chequeFile, tanFile, incFile, declarationFile,
      partnershipFile, passbookFile, addressFile, swiftFile, aadhaarFile, altDocFile, mandateFile,
      gstFileLink, panFileLink, chequeFileLink, tanFileLink, incFileLink, declarationFileLink,
      partnershipFileLink, passbookFileLink, addressFileLink, swiftFileLink, aadhaarFileLink, altDocFileLink, mandateFileLink,
      gstDetails, panDetails, chequeDetails, tanDetails, aadhaarDetails,
      bankDetails, hasNoCheque, altDocType, additionalAddress, checked, additional: newAdditional, leadId, manualTanNumber
    }));
  };

  // TAN Number Handler
  const handleTanNumberChange = (value) => {
    setManualTanNumber(value);
    
    // Auto-save manual TAN changes
    updateDraftData(getCompleteFormState({
      activeStep, formData, entityIdType, entityIdNumber, entityIdError,
      gstFile, panFile, chequeFile, tanFile, incFile, declarationFile,
      partnershipFile, passbookFile, addressFile, swiftFile, aadhaarFile, altDocFile, mandateFile,
      gstFileLink, panFileLink, chequeFileLink, tanFileLink, incFileLink, declarationFileLink,
      partnershipFileLink, passbookFileLink, addressFileLink, swiftFileLink, aadhaarFileLink, altDocFileLink, mandateFileLink,
      gstDetails, panDetails, chequeDetails, tanDetails, aadhaarDetails,
      bankDetails, hasNoCheque, altDocType, additionalAddress, checked, additional, leadId,
      manualTanNumber: value
    }));
  };

  // Preview
  const handleOpenPreview = (label, file) => {
    setPreviewLabel(label);
    setPreviewFile(file);
    setPreviewOpen(true);
  };
  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  // Helper function to check if document is available (file or restored link)
  const hasDocument = (file, fileLink) => {
    return Boolean(file || fileLink);
  };

  // Helper function to check if document with OCR data is available
  const hasDocumentWithData = (file, fileLink, ocrData) => {
    return Boolean((file || fileLink) && ocrData);
  };

  // Helper function to check if TAN document is valid (either OCR extracted or manually entered)
  const hasTanDocumentWithData = (file, fileLink, tanDetails, manualTanNumber) => {
    return Boolean((file || fileLink) && (tanDetails?.tanNumber || manualTanNumber));
  };

  // Validation flags
  const isProprietorUnreg =
    formData.statusOfCustomer === 'Proprietor / Individual' &&
    formData.gstStatus === 'Un-Registered';


  // inside App(), after you compute isProprietorUnreg:
  const isPrivateReg =
    formData.statusOfCustomer === 'Private Limited/ Public Limited';

  // const isUnregistered = formData.gstStatus === 'Un-Registered';

  const isPartnershipReg =
    formData.statusOfCustomer === 'Partnership/LLP' &&
    formData.gstStatus === 'Normal Registered';

  const isPartnershipUnreg =
    formData.statusOfCustomer === 'Partnership/LLP' &&
    formData.gstStatus === 'Un-Registered';

  const isSocietyUnreg =
    formData.statusOfCustomer === 'Society/AOP/HUF/Trust' &&
    formData.gstStatus === 'Un-Registered';

  const isLLPUnreg =
    formData.statusOfCustomer === 'LLP' &&
    formData.gstStatus === 'Un-Registered';

  const isRegPartnershipUnReg =
    formData.statusOfCustomer === 'Registered Partnership' &&
    formData.gstStatus === 'Un-Registered';

  const isUnRegPartnershipUnReg =
    formData.statusOfCustomer === 'Unregistered Partnership' &&
    formData.gstStatus === 'Un-Registered';

  const isStep0Valid =
    // 1) Must pick a customer status and a GST status
    Boolean(formData.statusOfCustomer && formData.gstStatus)
    // 2) Only require GST-Type when GST is NOT Un-Registered
    && (
      formData.gstStatus === 'Un-Registered'
      || Boolean(formData.gstRegistrationType)
    )
    // 3) If Private-entity OR Partnership/LLP, also require that dropdown + number
    && (
      (
        formData.statusOfCustomer !== statusOptions[1] &&
        formData.statusOfCustomer !== statusOptions[3] &&
        formData.statusOfCustomer !== statusOptions[4]
      )
      || (entityIdType && entityIdNumber && !entityIdError)
    );

  const isStep1Valid =
    formData.statusOfCustomer === 'Foreign Entity'
      ? (
        hasDocument(swiftFile, swiftFileLink)
      ) // All document uploads optional for Foreign Entity
      : (
        // proprietors + any unregistered private or partnership
        (isProprietorUnreg ? (
          hasDocument(declarationFile, declarationFileLink) &&
          hasDocumentWithData(panFile, panFileLink, panDetails?.panNumber) &&
          altDocType &&
          hasDocument(altDocFile, altDocFileLink) &&
          (
            (hasDocument(chequeFile, chequeFileLink) && chequeDetails?.ifsc) ||
            bankDetails.ifsc
          )
        ) :
          (isPartnershipUnreg
            ? (
                hasDocument(declarationFile, declarationFileLink) &&
                hasDocumentWithData(panFile, panFileLink, panDetails?.panNumber) &&
                hasDocumentWithData(aadhaarFile, aadhaarFileLink, aadhaarDetails?.address) &&
                (
                  (hasDocument(chequeFile, chequeFileLink) && chequeDetails?.ifsc) ||
                  bankDetails.ifsc
                )
              )
            // partnership + registered
            : isPartnershipReg ? (
                  hasDocumentWithData(gstFile, gstFileLink, gstDetails?.registrationNumber) &&
                  hasDocumentWithData(panFile, panFileLink, panDetails?.panNumber) &&
                  hasTanDocumentWithData(tanFile, tanFileLink, tanDetails, manualTanNumber) &&
                  hasDocument(partnershipFile, partnershipFileLink) &&
                  (
                    (hasDocument(chequeFile, chequeFileLink) && chequeDetails?.ifsc) ||
                    (bankDetails.ifsc && (!hasNoCheque || hasDocument(mandateFile, mandateFileLink)))
                  )
                )
            // private + registered
            : isPrivateReg ? (
                  hasDocumentWithData(gstFile, gstFileLink, gstDetails?.registrationNumber) &&
                  hasDocumentWithData(panFile, panFileLink, panDetails?.panNumber) &&
                  hasTanDocumentWithData(tanFile, tanFileLink, tanDetails, manualTanNumber) &&
                  hasDocument(incFile, incFileLink) &&
                  (
                    (hasDocument(chequeFile, chequeFileLink) && chequeDetails?.ifsc) ||
                    (bankDetails.ifsc && (!hasNoCheque || hasDocument(mandateFile, mandateFileLink)))
                  )
                )
            // society unreg
            : isSocietyUnreg ? (
                  hasDocument(declarationFile, declarationFileLink) &&
                  hasDocumentWithData(panFile, panFileLink, panDetails?.panNumber) &&
                  // hasDocument(passbookFile, passbookFileLink) &&
                  (
                    (hasDocument(chequeFile, chequeFileLink) && chequeDetails?.ifsc) ||
                    (bankDetails.ifsc && (!hasNoCheque || hasDocument(mandateFile, mandateFileLink)))
                  )
                )
            // LLP unreg
            : isLLPUnreg ? (
                  hasDocument(declarationFile, declarationFileLink) &&
                  hasDocumentWithData(panFile, panFileLink, panDetails?.panNumber) &&
                  hasDocument(incFile, incFileLink) &&
                  // hasDocument(passbookFile, passbookFileLink) &&
                  (
                    (hasDocument(chequeFile, chequeFileLink) && chequeDetails?.ifsc) ||
                    (bankDetails.ifsc && (!hasNoCheque || hasDocument(mandateFile, mandateFileLink)))
                  )
                )
            // Reg Partnership unreg
            : isRegPartnershipUnReg ? (
                  hasDocument(declarationFile, declarationFileLink) &&
                  hasDocumentWithData(panFile, panFileLink, panDetails?.panNumber) &&
                  hasDocument(partnershipFile, partnershipFileLink) &&
                  // hasDocument(passbookFile, passbookFileLink) &&
                  (
                    (hasDocument(chequeFile, chequeFileLink) && chequeDetails?.ifsc) ||
                    (bankDetails.ifsc && (!hasNoCheque || hasDocument(mandateFile, mandateFileLink)))
                  )
                )
            // Unreg Partnership unreg
            : isUnRegPartnershipUnReg ? (
                  hasDocument(declarationFile, declarationFileLink) &&
                  hasDocumentWithData(panFile, panFileLink, panDetails?.panNumber) &&
                  // hasDocument(passbookFile, passbookFileLink) &&
                  (
                    (hasDocument(chequeFile, chequeFileLink) && chequeDetails?.ifsc) ||
                    (bankDetails.ifsc && (!hasNoCheque || hasDocument(mandateFile, mandateFileLink)))
                  )
                )
            // fallback
            : (
                  hasDocumentWithData(gstFile, gstFileLink, gstDetails?.registrationNumber) &&
                  hasDocumentWithData(panFile, panFileLink, panDetails?.panNumber) &&
                  (
                    (hasDocument(chequeFile, chequeFileLink) && chequeDetails?.ifsc) ||
                    (bankDetails.ifsc && (!hasNoCheque || hasDocument(mandateFile, mandateFileLink)))
                  )
                )
          )
        )
      );

  const isStep3Valid =
    additional.email &&
    /^[0-9]{10}$/.test(additional.mobile) &&
    additional.contactName &&
    // /^[0-9]{10}$/.test(additional.contactMobile) &&
    additional.certifyTrue &&
    additional.acceptPrivacy;

  // Get draft progress info - memoized to prevent infinite renders
  const [draftProgressInfo, setDraftProgressInfo] = useState(null);
  
  useEffect(() => {
    if (draftInfo.hasDraft) {
      const draftData = loadDraft();
      if (draftData) {
        setDraftProgressInfo(getProgressSummary(draftData));
      }
    } else {
      setDraftProgressInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftInfo.hasDraft]); // Intentionally limited to prevent infinite loops

  // Draft restoration functions
  const handleLoadDraft = () => {
    const draftData = loadDraft();
    if (draftData && shouldShowDraftDialog(draftData)) {
      const setters = {
        setActiveStep,
        setFormData,
        setEntityIdType,
        setEntityIdNumber,
        setEntityIdError,
        
        setGstFileLink,
        setPanFileLink,
        setChequeFileLink,
        setTanFileLink,
        setIncFileLink,
        setDeclarationFileLink,
        setPartnershipFileLink,
        setPassbookFileLink,
        setAddressFileLink,
        setSwiftFileLink,
        setAadhaarFileLink,
        setAltDocFileLink,
        setMandateFileLink,
        
        setGstDetails,
        setPanDetails,
        setChequeDetails,
        setTanDetails,
        setAadhaarDetails,
        
        setManualTanNumber,
        
        setBankDetails,
        setHasNoCheque,
        setAltDocType,
        setAdditionalAddress,
        setChecked,
        setAdditional,
      };
      
      const success = applyFormState(draftData, setters);
      if (success) {
        setSnack({
          open: true,
          message: 'Draft restored successfully!',
          severity: 'success'
        });
        hideRestoreDialog();
        // Refresh progress info after restoration
        setDraftProgressInfo(getProgressSummary(draftData));
      } else {
        setSnack({
          open: true,
          message: 'Failed to restore draft.',
          severity: 'error'
        });
      }
    }
  };

  const handleClearDraft = () => {
    clearDraft();
    hideRestoreDialog();
    setDraftProgressInfo(null);
    setSnack({
      open: true,
      message: 'Draft deleted.',
      severity: 'info'
    });
  };

  const handleNext = () => {
    if (activeStep === 0 && !isStep0Valid) {
      setSnack({
        open: true,
        message: 'Please fill in all required fields in Customer Details before proceeding.',
        severity: 'error'
      });
      return;
    }
    if (activeStep === 1 && !isStep1Valid) {
      setSnack({
        open: true,
        message: 'Please upload valid documents (OCR must read them) before proceeding.',
        severity: 'error'
      });
      return;
    }
    const newActiveStep = activeStep + 1;
    setActiveStep(newActiveStep);
    
    // Auto-save step changes
    updateDraftData(getCompleteFormState({
      activeStep: newActiveStep, formData, entityIdType, entityIdNumber, entityIdError,
      gstFile, panFile, chequeFile, tanFile, incFile, declarationFile,
      partnershipFile, passbookFile, addressFile, swiftFile, aadhaarFile, altDocFile, mandateFile,
      gstFileLink, panFileLink, chequeFileLink, tanFileLink, incFileLink, declarationFileLink,
      partnershipFileLink, passbookFileLink, addressFileLink, swiftFileLink, aadhaarFileLink, altDocFileLink, mandateFileLink,
      gstDetails, panDetails, chequeDetails, tanDetails, aadhaarDetails,
      bankDetails, hasNoCheque, altDocType, additionalAddress, checked, additional, leadId, manualTanNumber
    }));
  };
  
  const handleBack = () => {
    const newActiveStep = activeStep - 1;
    setActiveStep(newActiveStep);
    
    // Auto-save step changes
    updateDraftData(getCompleteFormState({
      activeStep: newActiveStep, formData, entityIdType, entityIdNumber, entityIdError,
      gstFile, panFile, chequeFile, tanFile, incFile, declarationFile,
      partnershipFile, passbookFile, addressFile, swiftFile, aadhaarFile, altDocFile, mandateFile,
      gstFileLink, panFileLink, chequeFileLink, tanFileLink, incFileLink, declarationFileLink,
      partnershipFileLink, passbookFileLink, addressFileLink, swiftFileLink, aadhaarFileLink, altDocFileLink, mandateFileLink,
      gstDetails, panDetails, chequeDetails, tanDetails, aadhaarDetails,
      bankDetails, hasNoCheque, altDocType, additionalAddress, checked, additional, leadId, manualTanNumber
    }));
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (activeStep < steps.length - 1) {
      setSnack({
        open: true,
        message: 'Please complete all steps before submitting.',
        severity: 'error'
      });
      return;
    }
    if (!isStep3Valid) {
      setSnack({
        open: true,
        message: 'Please fill all required fields in Additional Information before submitting.',
        severity: 'error'
      });
      return;
    }

    // ▶ block double-submit entirely on the client
    if (leadId && localStorage.getItem(`submitted_${leadId}`)) {
      setSnack({
        open: true,
        message: 'You have already submitted this form for this lead.',
        severity: 'warning'
      });
      return;
    }

    // 1) pull raw address
    const rawAddress =
      gstDetails?.principalPlaceAddress ??
      aadhaarDetails?.address ??
      '';

    // 2) build payload
    // Use manual TAN number if provided, otherwise use extracted TAN
    const finalTanDetails = {
      ...tanDetails,
      tanNumber: manualTanNumber || tanDetails?.tanNumber || ''
    };
    
    const payload = {
      formData,
      additionalAddress,
      bankDetails,
      gstDetails,
      addressDetails: parseAddress(rawAddress),
      aadhaarRawAddress: aadhaarDetails?.address || '',
      panDetails,
      chequeDetails,
      partnershipFileLink,
      tanDetails: finalTanDetails,
      digitalSignature: digitalSigFile,
      additional,
      entityIdType,
      entityIdNumber,
      gstFileLink,
      panFileLink,
      chequeFileLink,
      declarationFileLink,
      tanFileLink,
      incFileLink,
      aadhaarFileLink,
      passbookFileLink,
      addressFileLink,
      mandateFileLink,
      swiftFileLink,
      type_of_document: altDocType ? (altDocType === 'passport' ? 'Passport' : altDocType === 'dl' ? 'DL' : 'Voter ID') : undefined,
      copy_of_passport: altDocType === 'passport' ? altDocFileLink : undefined,
      copy_of_dl: altDocType === 'dl' ? altDocFileLink : undefined,
      copy_of_voter_id: altDocType === 'voter_id' ? altDocFileLink : undefined
    };


    // 3) pick endpoint (POST for new, PATCH for existing lead)
    const endpoint =
      `${API_URL}/onboarding-submit` +
      (leadId ? `?lead_id=${leadId}` : '');
    setLoading(true);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-TOKEN': API_TOKEN },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      setLoading(false);
      
      if (!response.ok) {
        // if our server returned the 400 "already submitted" error, show warning
        if (response.status === 400 && result.error === 'Already submitted for this lead') {
          setSnack({
            open: true,
            message: "You've already submitted for this lead.",
            severity: 'warning',
          });
        } else {
          throw new Error(result.error || 'Submission failed');
        }
        return;
      }

      // mark locally so even reloads / new tabs know we've sent it
      if (leadId) {
        localStorage.setItem(`submitted_${leadId}`, 'true');
      }
      setSubmitted(true);

    } catch (err) {
      console.error('Error submitting form:', err);
      setSnack({
        open: true,
        message: err.message || 'Submission failed. Please try again.',
        severity: 'error'
      });
    }
  };

  // ▶ if we've already done this lead, don't show the form again
  if (alreadySubmitted) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          {errorMsg}
        </Typography>
        <Typography>
          If you need to make changes, please contact support.
        </Typography>
      </Container>
    );
  }

  // ▶ once they just now submitted successfully, show success
  if (submitted) {
    localStorage.clear(); // clear localStorage to reset state
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="h4" color="primary" gutterBottom>
          🎉 Submission Successful!
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          Thank you for completing your onboarding. Your details have been sent successfully.
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
        >
          Okay
        </Button>
      </Container>
    );
  }

  const closeSnack = () => setSnack(s => ({ ...s, open: false }));

  if (loading) return <FullPageLoader />;

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
        <Paper elevation={3} sx={{ p: 4, borderTop: `4px solid ${theme.palette.primary.main}` }}>
          {/* Header & Stepper */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <img
              src={`${process.env.PUBLIC_URL}/jioLogo.png`}
              alt="Logo"
              style={{ height: 60 }}
            />
          </Box>
          <Typography variant="h4" align="center" color="primary" gutterBottom >
            Customer Onboarding Form
          </Typography>
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 4,
              display: 'flex',
              overflowX: 'auto',
              flexWrap: 'nowrap',
              '& .MuiStep-root': { flex: '0 0 auto' } // prevent shrinking
            }}
            connector={<Divider sx={{ bgcolor: 'secondary.main' }} />}
          >
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box component="form" onSubmit={handleSubmit}>
            {/* Step 0 */}
            {activeStep === 0 && (
              <CustomerDetailsForm
                formData={formData}
                onChange={handleChange}
                statusOptions={statusOptions}
                gstStatusOptions={gstStatusOptions}
                gstRegTypeOptions={gstRegTypeOptions}
                entityIdType={localStorage.getItem('entityIdType_' + leadId) || entityIdType}
                entityIdNumber={entityIdNumber}
                entityIdError={entityIdError}
                entityTypeOptions={entityTypeOptions}
                onEntityIdTypeChange={e => {
                  setEntityIdType(e.target.value);
                  setEntityIdNumber('');
                  localStorage.setItem('entityIdType_'+ leadId, e.target.value);
                  localStorage.setItem('entityIdNumber_'+ leadId, '');
                }}
                onEntityIdNumberChange={e => {
                  const val = e.target.value;
                  setEntityIdNumber(val);
                  localStorage.setItem('entityIdNumber_'+ leadId, val);
                  if (entityIdError) setEntityIdError('');
                }}
                onEntityIdBlur={() => {
                  if (!validateEntityId(entityIdType, entityIdNumber)) {
                    setEntityIdError(`Invalid ${entityIdType}`);
                  }
                }}
              />
            )}

            {/* Step 1 */}
            {activeStep === 1 && (
              <>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 3, mt: -1, fontWeight: '600', fontStyle: 'italic' , color: 'red' , fontSize: '12px'}}>
                Please upload the required documents. OCR will extract key details automatically. 
                For best results, upload clear images or PDFs of the original documents.
              </Typography>
              <DocumentUploadForm
                formData={formData}
                files={{
                  gstFile,
                  panFile,
                  chequeFile,
                  tanFile,
                  declarationFile,
                  incFile,
                  partnershipFile,
                  // passbookFile,
                  addressFile,
                  swiftFile,
                  aadhaarFile,
                  altDocFile
                }}
                fileLinks={{
                  gstFileLink,
                  panFileLink,
                  chequeFileLink,
                  tanFileLink,
                  declarationFileLink,
                  incFileLink,
                  partnershipFileLink,
                  // passbookFileLink,
                  addressFileLink,
                  swiftFileLink,
                  aadhaarFileLink,
                  altDocFileLink
                }}
                details={{
                  gstDetails,
                  panDetails,
                  chequeDetails,
                  tanDetails,
                  aadhaarDetails
                }}
                onFileUpload={handleFileUpload}
                onPreview={handleOpenPreview}
                hasNoCheque={hasNoCheque}
                onChequeCheckboxChange={handleCheckboxCheque}
                bankDetails={bankDetails}
                onBankInputChange={handleBankInputChange}
                bankDialogOpen={bankDialogOpen}
                onBankDialogClose={handleBankDialogClose}
                onBankDialogOpen={() => setBankDialogOpen(true)}
                altDocType={altDocType}
                onAltDocTypeChange={e => {
                  setAltDocType(e.target.value);
                  setAltDocFile(null);
                  setAltDocFileLink('');
                }}
                mandateFile={mandateFile}
                manualTanNumber={manualTanNumber}
                onTanNumberChange={handleTanNumberChange}
              />
              </>
            )}

            {/* Step 2 */}
            {activeStep === 2 && (
              <ReviewSection
                formData={formData}
                panDetails={panDetails}
                gstDetails={gstDetails}
                tanDetails={tanDetails}
                chequeDetails={chequeDetails}
                bankDetails={bankDetails}
                aadhaarDetails={aadhaarDetails}
                altDocType={altDocType}
                incFileLink={incFileLink}
                partnershipFileLink={partnershipFileLink}
                // passbookFileLink={passbookFileLink}
                parseAddress={parseAddress}
                additionalAddress={additionalAddress}
                checked={checked}
                onCheckboxChange={handleCheckboxChange}
                open={open}
                onClose={handleClose}
                onInputChange={handleInputChange}
              />
            )}

            {/* Step 3 */}
            {activeStep === 3 && (
              <AdditionalInfoForm
                additional={additional}
                onChange={handleAdditionalChange}
                countryCodes={countryCodes}
              />
            )}

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button disabled={activeStep === 0} onClick={handleBack}>
                Back
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={activeStep === 0
                    ? !isStep0Valid
                    : activeStep === 1
                      ? !isStep1Valid
                      : false
                  }
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={!isStep3Valid}
                >
                  Submit
                </Button>
              )}
            </Box>
          </Box>

          {/* Draft Indicator */}
          <DraftIndicator
            draftInfo={draftInfo}
            onLoadDraft={handleLoadDraft}
            onClearDraft={handleClearDraft}
            onSaveDraft={saveDraft}
            showDialog={draftInfo.showRestoreDialog}
            onDialogClose={hideRestoreDialog}
            draftProgressInfo={draftProgressInfo}
          />

          {/* Preview Dialog */}
          <PreviewDialog
            open={previewOpen}
            onClose={handleClosePreview}
            file={previewFile}
            label={previewLabel}
          />

          {/* Snackbar */}
          <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack}>
            <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: '100%' }}>
              {snack.message}
            </Alert>
          </Snackbar>
        </Paper>
      </Container>
    </ThemeProvider>
  );
}
